// Guarded wrapper around amazon-connect-streams.
//
// The CCP (Contact Control Panel) is embedded by Amazon Connect into an iframe
// inside a host <div>. All of this is ENV-GATED: if VITE_CONNECT_CCP_URL is
// unset, or the streams global is unavailable, we degrade gracefully and the
// workspace renders a clear "not configured" placeholder instead of crashing.

// NOTE: amazon-connect-streams is NOT imported as a module. It is written in
// sloppy mode (implicit-global assignments like `publishTelemetryEvent = ...`)
// which throws `ReferenceError: assignment to undeclared variable` when bundled
// into an ESM (always strict) — that blanked the whole app. AWS ships it to be
// loaded as a classic <script>, so we lazy-inject the vendored build below only
// when the agent workspace actually needs the CCP.
const STREAMS_SRC = '/vendor/connect-streams.min.js';
let streamsPromise: Promise<ConnectGlobal | undefined> | null = null;

function loadStreams(): Promise<ConnectGlobal | undefined> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(undefined);
  }
  if (window.connect?.core) return Promise.resolve(window.connect);
  if (streamsPromise) return streamsPromise;

  streamsPromise = new Promise((resolve) => {
    const ready = (): void => resolve(window.connect?.core ? window.connect : undefined);
    const existing = document.querySelector<HTMLScriptElement>('script[data-tips-crm-streams]');
    if (existing) {
      existing.addEventListener('load', ready);
      existing.addEventListener('error', () => resolve(undefined));
      return;
    }
    const s = document.createElement('script');
    s.src = STREAMS_SRC;
    s.async = true;
    s.setAttribute('data-tips-crm-streams', '');
    s.onload = ready;
    s.onerror = () => {
      // eslint-disable-next-line no-console
      console.error('[ccp] failed to load connect-streams from', STREAMS_SRC);
      resolve(undefined);
    };
    document.head.appendChild(s);
  });
  return streamsPromise;
}

export interface ActiveContactInfo {
  contactId: string;
  attributes: Record<string, string>;
  status: string;
}

export type ContactListener = (contact: ActiveContactInfo | null) => void;
export type AgentListener = (agentName: string, state: string) => void;

let initialized = false;

export function getCcpUrl(): string | undefined {
  const url = import.meta.env.VITE_CONNECT_CCP_URL;
  return url && url.trim() ? url.trim() : undefined;
}

export function isCcpConfigured(): boolean {
  return Boolean(getCcpUrl());
}

/**
 * The SAML login URL for the embedded CCP. The Connect instance is SAML (IdP-initiated
 * only), so the agent cannot log in at the CCP directly — that yields "Session expired".
 * This points at the API's /connect/ccp-login bridge, which redirects through the
 * ZITADEL IdP and federates the agent into Connect. Returned only when the CCP is
 * configured. Resolves a relative API base against the current origin (the web nginx
 * proxies /api -> crm-api, so tipscontact.com/api/connect/ccp-login reaches the API).
 */
export function getCcpLoginUrl(): string | undefined {
  if (!getCcpUrl()) return undefined;
  const base = import.meta.env.VITE_API_URL || '/api';
  const apiBase = base.startsWith('http') ? base : `${window.location.origin}${base}`;
  return `${apiBase.replace(/\/$/, '')}/connect/ccp-login`;
}

/**
 * Open the Amazon Connect softphone in a dedicated first-party window.
 *
 * The embedded CCP iframe is cross-origin (tipscontact.com hosting tipsdata.my.connect.aws);
 * modern browsers block its session via third-party-cookie / frame-ancestors restrictions,
 * so the embed can silently fail to authenticate. Opening the SAML login bridge in a real
 * window runs the CCP in a first-party context where login + the softphone always work, and
 * amazon-connect-streams shares that agent session with the embedded panel via its
 * SharedWorker (same Connect origin). Must be called from a user gesture (else popup-blocked).
 */
export function openCcpSignInWindow(): Window | null {
  const url = getCcpLoginUrl();
  if (!url) return null;
  const w = 420;
  const h = 660;
  const left = Math.max(0, (window.screen.width - w) / 2);
  const top = Math.max(0, (window.screen.height - h) / 2);
  return window.open(
    url,
    'tips-connect-ccp',
    `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no`,
  );
}

function getConnect(): ConnectGlobal | undefined {
  // The library attaches itself to window.connect once loaded. It may be
  // undefined in non-browser/test contexts — callers handle that gracefully.
  if (typeof window !== 'undefined' && window.connect?.core) {
    return window.connect;
  }
  return undefined;
}

/**
 * Initialize the embedded CCP into the given container. Safe to call when
 * Connect is not configured — returns false and does nothing.
 */
export async function initCcp(container: HTMLElement): Promise<boolean> {
  const ccpUrl = getCcpUrl();
  if (!ccpUrl) {
    // eslint-disable-next-line no-console
    console.warn('[ccp] VITE_CONNECT_CCP_URL not set — CCP disabled (dormant mode).');
    return false;
  }
  const c = await loadStreams();
  if (!c) {
    // eslint-disable-next-line no-console
    console.warn('[ccp] amazon-connect-streams unavailable — CCP disabled.');
    return false;
  }
  if (initialized) {
    return true;
  }
  try {
    c.core.initCCP(container, {
      ccpUrl,
      // SAML instance: route the login popup through the IdP bridge instead of the
      // CCP's own (dead) login page, which only shows "Session expired".
      loginUrl: getCcpLoginUrl(),
      loginPopup: true,
      loginPopupAutoClose: true,
      softphone: { allowFramedSoftphone: true },
      // The CRM plays its own looping ringtone for offered contacts (ringtone.ts),
      // so suppress Connect's built-in chat ring to avoid a double alert.
      chat: { disableRingtone: true },
      pageOptions: {
        enableAudioDeviceSettings: true,
        enablePhoneTypeSettings: true,
      },
    });
    initialized = true;
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[ccp] initCCP failed', err);
    return false;
  }
}

function flattenAttributes(raw: Record<string, ConnectAttributeValue>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of Object.keys(raw)) {
    const entry = raw[key];
    if (entry && typeof entry.value === 'string') {
      out[entry.name ?? key] = entry.value;
    }
  }
  return out;
}

/**
 * Subscribe to active-contact lifecycle. Returns a no-op cleanup when Connect
 * is unavailable. amazon-connect-streams has no unsubscribe API, so the cleanup
 * here is a guard flag the caller can use to stop reacting after unmount.
 */
export function subscribeToContacts(listener: ContactListener): () => void {
  const c = getConnect();
  if (!c) return () => {};
  let active = true;

  c.contact((contact) => {
    if (!active) return;
    const emit = () => {
      if (!active) return;
      listener({
        contactId: contact.getContactId(),
        attributes: flattenAttributes(contact.getAttributes()),
        status: contact.getStatus().type,
      });
    };
    contact.onConnecting(emit);
    contact.onConnected(emit);
    contact.onEnded(() => {
      if (active) listener(null);
    });
    contact.onDestroy(() => {
      if (active) listener(null);
    });
  });

  return () => {
    active = false;
  };
}

/** An offered (ringing) contact the agent can Accept or Reject. */
export interface IncomingContact {
  contactId: string;
  /** 'chat' | 'voice' | 'queue_callback' | 'task' | 'unknown'. */
  channel: string;
  attributes: Record<string, string>;
  /** Answer the contact (resolves on Connect's success callback). */
  accept(): Promise<void>;
  /** Decline the contact — returns it to the queue for another agent. */
  reject(): Promise<void>;
}

export type IncomingListener = (incoming: IncomingContact | null) => void;

function contactType(contact: ConnectContact): string {
  try {
    return typeof contact.getType === 'function' ? contact.getType() : 'unknown';
  } catch {
    return 'unknown';
  }
}

function acceptVia(contact: ConnectContact): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    try {
      contact.accept({ success: () => resolve(), failure: (e) => reject(e) });
    } catch (e) {
      reject(e);
    }
  });
}

function rejectVia(contact: ConnectContact): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    try {
      contact.reject({ success: () => resolve(), failure: (e) => reject(e) });
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * Subscribe to OFFERED (ringing) contacts so the workspace can alert the agent with a
 * looping ringtone + Accept/Reject controls. Emits an {@link IncomingContact} while a
 * contact is being offered (`connecting`/`incoming`), and `null` once it's accepted,
 * connected, missed, ended, or destroyed (i.e. no longer ringing). Returns a no-op
 * cleanup when Connect is unavailable.
 */
export function subscribeToIncoming(listener: IncomingListener): () => void {
  const c = getConnect();
  if (!c) return () => {};
  let active = true;

  c.contact((contact) => {
    if (!active) return;
    const offer = (): void => {
      if (!active) return;
      listener({
        contactId: contact.getContactId(),
        channel: contactType(contact),
        attributes: flattenAttributes(contact.getAttributes()),
        accept: () => acceptVia(contact),
        reject: () => rejectVia(contact),
      });
    };
    const clear = (): void => {
      if (active) listener(null);
    };
    // Offered states.
    contact.onConnecting(offer);
    contact.onIncoming?.(offer);
    // No-longer-ringing states.
    contact.onAccepted?.(clear);
    contact.onConnected(clear);
    contact.onMissed?.(clear);
    contact.onEnded(clear);
    contact.onDestroy(clear);
  });

  return () => {
    active = false;
  };
}

export function subscribeToAgent(listener: AgentListener): () => void {
  const c = getConnect();
  if (!c) return () => {};
  let active = true;

  c.agent((agent) => {
    if (!active) return;
    listener(agent.getName(), agent.getState().name);
    agent.onStateChange((s) => {
      if (active) listener(agent.getName(), s.newState);
    });
  });

  return () => {
    active = false;
  };
}
